/**
 * TrendDetailPage Component
 * 
 * Built with shadcn/ui + Tailwind CSS.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../../components/ui/chart';
import {
  ArrowLeft,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  ChevronRight,
  Settings,
  Search as SearchIcon,
  Loader2,
  Newspaper,
  ExternalLink,
  Clock,
} from 'lucide-react';

import { ApiServices, newsService } from '../../services/ApiServices';
import CelebrityProfileCard, { CelebrityProfileSkeleton } from './CelebrityProfileCard';
import GeoFlag from '../../components/GeoFlag';
import { TIME_RANGES } from '../../constants/timeRanges';
import { CATEGORIES } from '../../constants/categories';
import { NEWS_CATEGORY_LABELS, NEWS_TAG_LABELS } from '../../constants/newsClassification';

// shadcn components
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { cn } from '../../lib/utils';

// ChartJS registration removed in favor of Recharts

const TrendDetailPage = () => {
  const { query } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const trendsBackLink = useMemo(() => {
    if (location.state === null || location.state === undefined) {
      return '/trends';
    }
    const listSearch = location.state.trendsListSearch;
    if (typeof listSearch === 'string' && listSearch.length > 0) {
      return { pathname: '/trends', search: `?${listSearch}` };
    }
    return '/trends';
  }, [location.state]);

  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [relatedNews, setRelatedNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  // "Who is this" card for people: { status: 'ok' | 'pending' | 'not_person' | 'error', profile }
  const [celebrityProfile, setCelebrityProfile] = useState(null);

  // Local state for settings dialog
  const [dialogTimeRange, setDialogTimeRange] = useState('now 1-d');
  const [dialogRegion, setDialogRegion] = useState('US');

  // URL Params State
  const searchParams = new URLSearchParams(location.search);
  const timeRange = searchParams.get('date') || 'now 1-d';
  const regionCode = searchParams.get('geo') || 'US';
  const urlCategory = searchParams.get('category') || null;
  const urlDomain = searchParams.get('domain') || null;

  // Effective geo/category/domain: from API trend_meta when available, else from URL
  const effectiveGeo = trend?.trend_meta?.geo || regionCode;
  const effectiveCategory = trend?.trend_meta?.state || urlCategory;
  const effectiveDomain = trend?.trend_meta?.domain || urlDomain;
  const isCelebrity = effectiveCategory === 'Celebrity';

  // State synchronization with dialog
  useEffect(() => {
    setDialogTimeRange(timeRange);
    setDialogRegion(regionCode);
  }, [timeRange, regionCode]);

  // Chart Logic
  const processChartData = useCallback((data) => {
    if (!data?.interest_over_time?.timeline_data) {
      setChartData([]);
      return;
    }

    const timelineData = data.interest_over_time.timeline_data;
    const baseQuery = decodeURIComponent(query).split('?')[0];

    const formattedData = timelineData.map(point => {
      let interest = 0;
      if (point.values?.length) {
        const match = point.values.find(v =>
          v?.query && decodeURIComponent(v.query.split('?')[0]).toLowerCase() === baseQuery.toLowerCase()
        );
        interest = match?.extracted_value || point.values[0]?.extracted_value || 0;
      }
      return {
        date: point.date || '',
        interest: interest
      };
    });

    setChartData(formattedData);
  }, [query]);

  // Fetch Logic
  const fetchTrendDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const apiParams = { date: timeRange, geo: regionCode };
      const data = await ApiServices.getTrendDetail(query, apiParams);

      if (data.error && !data.interest_over_time) {
        throw new Error(data.error);
      }

      setTrend(data);
      processChartData(data);
    } catch (err) {
      console.error("Error fetching trend details:", err);
      setError(err.message || 'Ошибка загрузки метрик тренда');
    } finally {
      setLoading(false);
    }
  }, [query, timeRange, regionCode, processChartData]); // Dependencies are primitives or stable

  useEffect(() => {
    fetchTrendDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTrendDetail]);

  useEffect(() => {
    ApiServices.trackAuditEvent('page_view', 'trend_details');
  }, []);

  useEffect(() => {
    const decodedQuery = decodeURIComponent(query).split('?')[0];
    setNewsLoading(true);
    newsService.getCorrelatedNews(decodedQuery, effectiveGeo, 50)
      .then(data => setRelatedNews(data || []))
      .catch(() => setRelatedNews([]))
      .finally(() => setNewsLoading(false));
  }, [query, effectiveGeo]);

  // The first request for a person starts a web search on the server (5-30 s): while the answer
  // is 'pending', ask again every 3 s, up to ~75 s
  useEffect(() => {
    if (!isCelebrity) {
      setCelebrityProfile(null);
      return undefined;
    }
    const name = decodeURIComponent(query).split('?')[0];
    let cancelled = false;
    let timer = null;
    let attempts = 0;
    setCelebrityProfile({ status: 'pending', profile: null });
    const load = () => {
      ApiServices.getCelebrityProfile(name, effectiveGeo)
        .then((data) => {
          if (cancelled) return;
          if (data?.status === 'pending' && attempts < 25) {
            attempts += 1;
            timer = setTimeout(load, 3000);
            return;
          }
          setCelebrityProfile(data?.status === 'pending' ? { status: 'error', profile: null } : data);
        })
        .catch(() => {
          if (!cancelled) setCelebrityProfile({ status: 'error', profile: null });
        });
    };
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, effectiveGeo, isCelebrity]);

  // Chart Logic


  // Metrics Calculations
  const averageVolume = useMemo(() => {
    if (!chartData || chartData.length === 0) return 30;
    const values = chartData.map(d => d.interest);
    return Math.floor(values.reduce((a, b) => a + b, 0) / values.length);
  }, [chartData]);

  const volumeChange = useMemo(() => {
    if (!chartData || chartData.length < 2) return null;
    const first = chartData[0].interest;
    const last = chartData[chartData.length - 1].interest;
    if (first === 0) return 0;
    return ((last - first) / first) * 100;
  }, [chartData]);

  const formatVolume = (vol) => {
    if (vol >= 1000000) return `${Math.floor(vol / 1000000)}M+`;
    if (vol >= 1000) return `${Math.floor(vol / 1000)}K+`;
    return `${vol || 30}+`;
  };

  // Event Handlers
  const handleStartResearch = async () => {
    try {
      const response = await ApiServices.startResearch(query, { geo: effectiveGeo });
      if (response.from_cache && response.content) {
        navigate(`/research?query=${encodeURIComponent(query)}&geo=${encodeURIComponent(effectiveGeo)}`, {
          state: {
            fromCache: true,
            query: decodeURIComponent(query),
            content: response.content,
            citations: response.citations || [],
            created_at: response.created_at,
            geo: effectiveGeo,
          },
        });
      } else if (response.task_id) {
        navigate(`/research?query=${encodeURIComponent(query)}&task_id=${response.task_id}&geo=${encodeURIComponent(effectiveGeo)}`);
      } else {
        throw new Error("Failed to start research");
      }
    } catch (err) {
      console.error(err);
      alert("Ошибка запуска исследования. Попробуйте снова.");
    }
  };

  const handleSettingsApply = () => {
    const params = new URLSearchParams();
    if (dialogRegion) params.append('geo', dialogRegion);
    if (dialogTimeRange) params.append('date', dialogTimeRange);
    navigate(`/trend/${encodeURIComponent(query)}?${params.toString()}`);
    setSettingsOpen(false);
  };

  // Chart configuration for Recharts
  const chartConfig = useMemo(() => ({
    interest: {
      label: "Интерес",
      color: "hsl(var(--primary))",
    }
  }), []);

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl space-y-6">

      {/* Settings Modal */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Настройки отображения</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Временной диапазон</label>
              <span className="text-xs text-muted-foreground ml-2">(Текущий: {TIME_RANGES.find(t => t.value === timeRange)?.label || timeRange})</span>
              <Select value={dialogTimeRange} onValueChange={setDialogTimeRange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_RANGES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Отмена</Button>
            <Button onClick={handleSettingsApply}>Применить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Breadcrumbs & Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center text-sm text-muted-foreground mb-2">
            <Link to={trendsBackLink} className="hover:text-primary transition-colors flex items-center">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Тренды
            </Link>
            <ChevronRight className="h-4 w-4 mx-2 opacity-50" />
            <span className="font-medium text-foreground truncate max-w-[200px]">{decodeURIComponent(query)}</span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight">{decodeURIComponent(query)}</h1>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Настройки
          </Button>
          <Button size="sm" onClick={handleStartResearch}>
            <SearchIcon className="h-4 w-4 mr-2" />
            Глубокое исследование
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin text-primary">
            <Settings className="h-12 w-12" />
          </div>
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error}. Часть данных может быть недоступна.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

            {/* Left: "who is this" for people, otherwise Overview Card */}
            {celebrityProfile?.status === 'ok' && celebrityProfile.profile ? (
              <CelebrityProfileCard
                key={celebrityProfile.profile.name}
                profile={celebrityProfile.profile}
                className="md:col-span-2"
              />
            ) : celebrityProfile?.status === 'pending' ? (
              <CelebrityProfileSkeleton className="md:col-span-2" />
            ) : (
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Обзор</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge>Активный тренд</Badge>
                    <span className="text-sm text-muted-foreground">
                      Начат недавно
                    </span>
                  </div>
                  <p className="text-muted-foreground">
                    Данные интереса для &quot;{decodeURIComponent(query)}&quot; в регионе {effectiveGeo}.
                    У тренда <strong>{averageVolume > 50 ? "высокий" : "умеренный"}</strong> поисковый интерес.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Right: Stats Card */}
            <Card>
              <CardHeader>
                <CardTitle>Объём</CardTitle>
              </CardHeader>
              <CardContent className="text-center pt-2">
                <div className="text-5xl font-bold mb-2">
                  {formatVolume(averageVolume)}
                </div>
                {volumeChange !== null && (
                  <div className={cn(
                    "flex items-center justify-center font-medium",
                    volumeChange >= 0 ? "text-green-500" : "text-red-500"
                  )}>
                    {volumeChange >= 0 ? <ArrowUp className="w-4 h-4 mr-1" /> : <ArrowDown className="w-4 h-4 mr-1" />}
                    {Math.abs(volumeChange).toFixed(1)}%
                  </div>
                )}
                <div className="mt-6 space-y-3 text-left">
                  <div className="flex justify-between text-sm py-2 border-b">
                    <span className="text-muted-foreground">Регион</span>
                    <Badge variant="secondary">{effectiveGeo}</Badge>
                  </div>
                  <div className="flex justify-between text-sm py-2 border-b">
                    <span className="text-muted-foreground">Период</span>
                    <Badge variant="secondary">{TIME_RANGES.find(t => t.value === timeRange)?.label || timeRange}</Badge>
                  </div>
                  <div className="flex justify-between text-sm py-2 border-b">
                    <span className="text-muted-foreground">Категория</span>
                    <Badge variant="secondary">
                      {effectiveCategory
                        ? (CATEGORIES.find(c => c.value === effectiveCategory.toLowerCase().replace(/\s+/g, '_'))?.label || effectiveCategory)
                        : 'Все'}
                    </Badge>
                  </div>
                  {effectiveDomain && effectiveDomain !== 'Other' && (
                    <div className="flex justify-between text-sm py-2 border-b">
                      <span className="text-muted-foreground">Сегмент</span>
                      <Badge variant="secondary">{effectiveDomain}</Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Chart Full Width */}
            <Card className="md:col-span-3">
              <CardHeader>
                <CardTitle>Интерес в динамике</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px] w-full">
                  {chartData && chartData.length > 0 ? (
                    <ChartContainer config={chartConfig} className="h-full w-full">
                      <AreaChart data={chartData}>
                        <defs>
                          <linearGradient id="interestGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="oklch(var(--primary))" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="oklch(var(--primary))" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="oklch(var(--muted-foreground))" strokeOpacity={0.4} />
                        <XAxis
                          dataKey="date"
                          tickLine={false}
                          axisLine={false}
                          tick={{ fill: "oklch(var(--muted-foreground))", fontSize: 12 }}
                          tickMargin={12}
                        />
                        <YAxis
                          tickLine={false}
                          axisLine={false}
                          tick={{ fill: "oklch(var(--muted-foreground))", fontSize: 12 }}
                          domain={[0, 100]}
                        />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Area
                          type="monotone"
                          dataKey="interest"
                          stroke="oklch(var(--primary))"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#interestGradient)"
                        />
                      </AreaChart>
                    </ChartContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-muted-foreground text-center px-4">
                      Нет данных для графика из Google Trends
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Related News */}
            {newsLoading ? (
              <Card className="md:col-span-3">
                <CardContent className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </CardContent>
              </Card>
            ) : relatedNews.length > 0 && (
              <Card className="md:col-span-3">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Newspaper className="h-5 w-5" />
                    Связанные новости
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-[600px] overflow-y-auto hide-scrollbar space-y-4">
                  {relatedNews.map((article) => (
                    <a
                      key={article.id}
                      href={article.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex gap-4 p-3 rounded-lg border hover:bg-muted/50 transition-colors group"
                    >
                      <div className="w-32 h-20 rounded overflow-hidden flex-shrink-0 bg-muted">
                        {article.image ? (
                          <img
                            src={article.image}
                            alt={article.title_ru || article.title}
                            referrerPolicy="no-referrer"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Newspaper className="h-6 w-6 text-muted-foreground/30" />
                          </div>
                        )}
                      </div>
                      <div className="flex-grow min-w-0">
                        <h4 className="font-medium text-sm leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                          {article.title_ru || article.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          {article.source_name && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                              {article.source_name}
                            </Badge>
                          )}
                          {article.category && (
                            <Badge className="bg-primary/90 text-primary-foreground text-[10px] px-1.5 py-0 h-4">
                              {NEWS_CATEGORY_LABELS[article.category] || article.category}
                            </Badge>
                          )}
                          {Array.isArray(article.tags) && article.tags
                            .filter(t => t !== 'other')
                            .map(t => (
                              <Badge key={t} variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                                {NEWS_TAG_LABELS[t] || t}
                              </Badge>
                            ))
                          }
                          {article.geo && <GeoFlag geo={article.geo} className="h-4" />}
                        </div>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                          {article.published_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(article.published_at).toLocaleDateString('ru-RU', {
                                day: 'numeric',
                                month: 'short',
                              })}
                            </span>
                          )}
                          <span className="flex items-center gap-1 group-hover:text-primary transition-colors ml-auto">
                            Источник
                            <ExternalLink className="h-3 w-3" />
                          </span>
                        </div>
                      </div>
                    </a>
                  ))}
                  </div>
                </CardContent>
              </Card>
            )}

          </div>
        </>
      )}
    </div>
  );
};

export default TrendDetailPage;